/**
 * Domain types and pure functions for the historical price data scraped into
 * Firebase Storage at `prices/<snapshot-folder>/<make>/<model>/filtered_data_v2.json`.
 *
 * The raw JSON encodes JS `Map`s as `{ dataType: 'Map', value: [...] }`; `reviveMaps`
 * below is the JSON.parse reviver that turns those back into real `Map`s.
 */

export interface EngineGearboxSplit {
  total: number;
  count: number;
  /** Lowest/highest asking price in this split; the scraper writes -1 when it has none. */
  cheapest?: number;
  priciest?: number;
}

export interface VariationStats {
  engineGearboxSplitInfo: Map<string, EngineGearboxSplit>;
}

/** generation name -> variation name -> stats */
export type GenerationStats = Map<string, VariationStats>;

export interface CountryPriceStats {
  mean: number;
  cheapest: number;
  priciest: number;
  count: number;
}

export interface YearPriceStats {
  median: number;
  count: number;
}

export interface OverallStats {
  mean: number;
  byCountry?: Map<string, CountryPriceStats>;
  /** Median asking price per production year (BGN); absent in snapshots older than this field. */
  byYear?: Map<number, YearPriceStats>;
}

/** generation name -> its variations, plus a special 'overall' entry for the whole model */
export type SnapshotData = Map<string, GenerationStats | OverallStats>;

export interface PriceSnapshot {
  date: Date;
  data: SnapshotData;
  folderName: string;
}

export interface GenerationVariationKey {
  generation: string;
  variation: string;
}

function isOverallStats(value: GenerationStats | OverallStats): value is OverallStats {
  return !(value instanceof Map);
}

export function reviveMaps(_key: string, value: unknown): unknown {
  if (typeof value === 'object' && value !== null && (value as { dataType?: string }).dataType === 'Map') {
    return new Map((value as { value: [unknown, unknown][] }).value);
  }
  return value;
}

/** Snapshot folders are named `mobile-<day>-<month>-<year>_<hour>-<minutes>`. */
export function parseSnapshotFolderDate(folderName: string): Date {
  const [, day, month, year] = folderName.split('_')[0].split('-');
  return new Date(Number(year), Number(month) - 1, Number(day));
}

export function sortSnapshotsByDate(snapshots: PriceSnapshot[]): PriceSnapshot[] {
  return [...snapshots].sort((a, b) => a.date.getTime() - b.date.getTime());
}

/** Repeats the earliest snapshot until there are at least `minCount` points, so the chart never looks too sparse. */
export function padSnapshotsToMinimum(snapshots: PriceSnapshot[], minCount = 12): PriceSnapshot[] {
  if (snapshots.length === 0) {
    return snapshots;
  }

  const padded = [...snapshots];
  while (padded.length < minCount) {
    padded.unshift(padded[0]);
  }
  return padded;
}

function averageEngineGearboxSplit(variation: VariationStats): number | null {
  let total = 0;
  let count = 0;
  for (const split of variation.engineGearboxSplitInfo.values()) {
    total += split.total;
    count += split.count;
  }
  const average = count > 0 ? total / count : 0;
  // The scraper writes -1 for ads without a usable price, so a non-positive average means "no data".
  return average > 0 ? average : null;
}

export function getOverallAverage(snapshot: PriceSnapshot): number | null {
  const overall = snapshot.data.get('overall');
  return overall && isOverallStats(overall) ? overall.mean : null;
}

export function getYearPriceStats(snapshot: PriceSnapshot): Map<number, YearPriceStats> | null {
  const overall = snapshot.data.get('overall');
  return overall && isOverallStats(overall) ? overall.byYear ?? null : null;
}

function getCountryStats(snapshot: PriceSnapshot): Map<string, CountryPriceStats> | null {
  const overall = snapshot.data.get('overall');
  return overall && isOverallStats(overall) ? overall.byCountry ?? null : null;
}

/**
 * Countries with price data in at least one snapshot, excluding 'Bulgaria' - the primary
 * "Average price" line already represents the whole (Bulgaria-dominated) market, and the point
 * of this filter is comparing against *other* countries.
 */
export function getAvailableCountries(snapshots: PriceSnapshot[]): string[] {
  const countries = new Set<string>();
  for (const snapshot of snapshots) {
    const byCountry = getCountryStats(snapshot);
    if (!byCountry) {
      continue;
    }
    for (const country of byCountry.keys()) {
      if (country !== 'Bulgaria') {
        countries.add(country);
      }
    }
  }
  return [...countries].sort();
}

export function getGenerationVariationAverage(
  snapshot: PriceSnapshot,
  generation: string,
  variation: string,
): number | null {
  const generationStats = snapshot.data.get(generation);
  if (!generationStats || isOverallStats(generationStats)) {
    return null;
  }

  const variationStats = generationStats.get(variation);
  return variationStats ? averageEngineGearboxSplit(variationStats) : null;
}

/** Ad-weighted average price across the given generations, or null when none of them has priced ads. */
export function getGenerationsAverage(snapshot: PriceSnapshot, generations: string[]): number | null {
  let total = 0;
  let count = 0;
  for (const generation of generations) {
    const stats = snapshot.data.get(generation);
    if (!stats || isOverallStats(stats)) {
      continue;
    }
    for (const variation of stats.values()) {
      if (averageEngineGearboxSplit(variation) === null) {
        continue;
      }
      for (const split of variation.engineGearboxSplitInfo.values()) {
        total += split.total;
        count += split.count;
      }
    }
  }
  return count > 0 ? total / count : null;
}

/**
 * `selector` is either `null`/`'overall'` for the whole-model average, or a
 * `"<generation>/<variation>"` key as used by the engine picker. Without an engine,
 * `generations` (when non-empty) narrows the average to those generations.
 */
export function computeAveragePrice(snapshot: PriceSnapshot, selector: string | null, generations: string[] = []): number | null {
  if (!selector || selector === 'overall') {
    return generations.length > 0 ? getGenerationsAverage(snapshot, generations) : getOverallAverage(snapshot);
  }

  const [generation, variation] = selector.split('/');
  return getGenerationVariationAverage(snapshot, generation, variation);
}

/** All generation/variation pairs present in a snapshot, excluding the 'overall' summary entry. */
export function getGenerationVariationKeys(snapshot: PriceSnapshot): GenerationVariationKey[] {
  const keys: GenerationVariationKey[] = [];

  for (const [generation, stats] of snapshot.data) {
    if (isOverallStats(stats)) {
      continue;
    }

    for (const variation of stats.keys()) {
      keys.push({ generation, variation });
    }
  }

  return keys;
}

export type PriceTrendDirection = 'up' | 'down' | 'flat';

export interface PriceTrend {
  changeAmount: number;
  changePercent: number;
  direction: PriceTrendDirection;
}

/** Compares the earliest vs. most recent snapshot average to show a "prices are rising/falling" signal. */
export function computePriceTrend(
  snapshots: PriceSnapshot[],
  selector: string | null,
  generations: string[] = [],
): PriceTrend | null {
  if (snapshots.length < 2) {
    return null;
  }

  const first = computeAveragePrice(snapshots[0], selector, generations);
  const last = computeAveragePrice(snapshots[snapshots.length - 1], selector, generations);
  if (first === null || last === null || first === 0) {
    return null;
  }

  const changeAmount = last - first;
  const changePercent = (changeAmount / first) * 100;
  const direction: PriceTrendDirection = changeAmount > 0 ? 'up' : changeAmount < 0 ? 'down' : 'flat';

  return { changeAmount, changePercent, direction };
}

export interface ChartSeries {
  labels: string[];
  values: number[];
}

const dateLabelFormatter = new Intl.DateTimeFormat('en-US', { day: 'numeric', month: 'short' });

/** Builds the label/value series `Chart` renders, rounding to whole currency units. */
export function buildChartSeries(snapshots: PriceSnapshot[], selector: string | null, generations: string[] = []): ChartSeries {
  return {
    labels: snapshots.map((snapshot) => dateLabelFormatter.format(snapshot.date)),
    values: snapshots.map((snapshot) => {
      const average = computeAveragePrice(snapshot, selector, generations);
      return average === null ? 0 : Math.round(average);
    }),
  };
}

/**
 * Per-country average price series, aligned to the same snapshots/labels as `buildChartSeries`.
 * Unlike the main series, missing data is `null` (not 0) so `Chart` can render a gap instead of
 * a misleading dip - expected for snapshots predating this feature, or a country with no ads
 * that period.
 */
export function buildCountryChartSeries(snapshots: PriceSnapshot[], country: string): Array<number | null> {
  return snapshots.map((snapshot) => {
    const stats = getCountryStats(snapshot)?.get(country);
    return stats ? Math.round(stats.mean) : null;
  });
}

/**
 * Average price across every non-Bulgaria country combined, weighted by each country's ad
 * count (mean * count sums back to the country's total price, since mean was itself total /
 * count) rather than a plain average-of-averages, so a country with more listings that period
 * counts for more. This is the default "how do prices abroad compare" line; picking specific
 * countries via the filter adds more lines alongside it rather than replacing it.
 */
export function buildAbroadChartSeries(snapshots: PriceSnapshot[]): Array<number | null> {
  return snapshots.map((snapshot) => {
    const byCountry = getCountryStats(snapshot);
    if (!byCountry) {
      return null;
    }

    let weightedTotal = 0;
    let totalCount = 0;
    for (const [country, stats] of byCountry) {
      if (country === 'Bulgaria') {
        continue;
      }
      weightedTotal += stats.mean * stats.count;
      totalCount += stats.count;
    }

    return totalCount > 0 ? Math.round(weightedTotal / totalCount) : null;
  });
}

export interface GenerationSummary {
  generation: string;
  /** Mean asking price (BGN) across every engine of this generation. */
  averagePrice: number;
  adCount: number;
  /** Lowest/highest asking price seen (BGN), or null when the snapshot has none. */
  cheapest: number | null;
  priciest: number | null;
  engineCount: number;
}

/** A cheapest price under this share of its average is treated as a placeholder, not a real ask. */
const MIN_PLAUSIBLE_PRICE_RATIO = 0.1;

/** Per-generation price, ad count and price range from one snapshot, cheapest generation first. */
export function summarizeGenerations(snapshot: PriceSnapshot): GenerationSummary[] {
  const summaries: GenerationSummary[] = [];

  for (const [generation, stats] of snapshot.data) {
    if (isOverallStats(stats)) {
      continue;
    }

    let total = 0;
    let adCount = 0;
    let cheapest: number | null = null;
    let priciest: number | null = null;
    for (const variation of stats.values()) {
      for (const split of variation.engineGearboxSplitInfo.values()) {
        total += split.total;
        adCount += split.count;
        // Joke prices ("1 лв", placeholders) would make the range useless, so a split's cheapest
        // price only counts when it's a plausible fraction of that split's own average.
        const splitAverage = split.count > 0 ? split.total / split.count : 0;
        if (split.cheapest !== undefined && split.cheapest >= splitAverage * MIN_PLAUSIBLE_PRICE_RATIO) {
          cheapest = cheapest === null ? split.cheapest : Math.min(cheapest, split.cheapest);
        }
        if (split.priciest !== undefined && split.priciest > 0) {
          priciest = priciest === null ? split.priciest : Math.max(priciest, split.priciest);
        }
      }
    }

    if (adCount > 0) {
      summaries.push({ generation, averagePrice: total / adCount, adCount, cheapest, priciest, engineCount: stats.size });
    }
  }

  return summaries.sort((a, b) => a.averagePrice - b.averagePrice);
}

/** Snapshots from the last `days` days before the latest one, for a "recent" price trend. */
export function recentSnapshots(snapshots: PriceSnapshot[], days: number): PriceSnapshot[] {
  const latest = snapshots[snapshots.length - 1];
  if (!latest) {
    return [];
  }
  const since = latest.date.getTime() - days * 24 * 60 * 60 * 1000;
  return snapshots.filter((snapshot, index) => snapshot.date.getTime() >= since && snapshots.indexOf(snapshot) === index);
}

/** "x5-iii-f15" -> "III F15": drops the model's own slug prefix from a scraped generation name. */
export function formatGenerationName(generation: string, model: string): string {
  const prefix = `${model.toLowerCase().replace(/\s+/g, '-')}-`;
  const name = generation.toLowerCase().startsWith(prefix) ? generation.slice(prefix.length) : generation;
  return name.replace(/[-_]+/g, ' ').trim().toUpperCase() || generation.toUpperCase();
}

/** The generation/variation key with the most ads in a snapshot, e.g. to preselect a sensible engine. */
export function mostCommonVariationKey(snapshot: PriceSnapshot, generations: string[] = []): string | null {
  let best: { key: string; count: number } | null = null;
  for (const [generation, stats] of snapshot.data) {
    if (isOverallStats(stats) || (generations.length > 0 && !generations.includes(generation))) {
      continue;
    }
    for (const [variation, variationStats] of stats) {
      let count = 0;
      for (const split of variationStats.engineGearboxSplitInfo.values()) {
        count += split.count;
      }
      if (averageEngineGearboxSplit(variationStats) !== null && (!best || count > best.count)) {
        best = { key: `${generation}/${variation}`, count };
      }
    }
  }
  return best?.key ?? null;
}
